import React from "react";
import { useNavigate } from "react-router-dom";
import { fileUrl } from "../lib/api";
import UserGem from "./UserGem";
import AvatarFrame from "./AvatarFrame";
import SmartImage from "./SmartImage";
import {
  User as UserIcon, MapPin, Instagram, Music2, Crown, Shield,
  Store, CheckCircle2, Sparkles,
} from "lucide-react";

/**
 * ProfileHeader — the redesigned Hidden Gem profile top-of-page.
 *
 * Renders:
 *   banner (cover) → overlapping avatar → name + @handle + badges → bio
 *   → stats (posts · followers · following) → action slot (children)
 *
 * Props:
 *   profile     Full user doc (works for /auth/me OR /users/{id})
 *   isOwner     bool
 *   actions     ReactNode — placed under the stats row (New Post primary, etc)
 *   equippedTitle  optional {label, color}  to show inline
 */
export default function ProfileHeader({ profile, isOwner, actions, equippedTitle }) {
  const navigate = useNavigate();
  if (!profile) return null;
  const cp = profile.customer_profile || {};
  const vp = profile.vendor_profile || {};
  const avatarPath = cp.avatar_url || vp.avatar_url;
  const avatarUrl = avatarPath ? fileUrl(avatarPath) : profile.picture;
  const bannerPath = cp.banner_url || vp.banner_url;
  const bannerUrl = bannerPath ? fileUrl(bannerPath) : null;
  const bannerMode = cp.banner_display_mode || vp.banner_display_mode || "smart_auto_fit";
  const handle = profile.username ? `@${profile.username}` : null;
  const bio = cp.bio || vp.bio || "";
  const role = profile.role || "customer";
  const postsCount = profile.community_posts_count ?? 0;
  const followers = profile.followers_count ?? 0;
  const following = profile.following_count ?? 0;

  const badges = [];
  if (profile.is_verified) badges.push({ key: "verified", label: "Verified", color: "#60A5FA", Icon: CheckCircle2 });
  if (role === "master_admin") badges.push({ key: "master", label: "Master", color: "#F59E0B", Icon: Crown });
  else if (role === "ceo") badges.push({ key: "ceo", label: "CEO", color: "#F59E0B", Icon: Crown });
  else if (role === "senior_admin") badges.push({ key: "sadmin", label: "Sr Admin", color: "#F59E0B", Icon: Shield });
  else if (role === "admin") badges.push({ key: "admin", label: "Admin", color: "#F59E0B", Icon: Shield });
  else if (role === "moderator" || role === "support") badges.push({ key: "mod", label: role.replace("_", " "), color: "#60A5FA", Icon: Shield });
  if (profile.is_vendor) badges.push({ key: "vendor", label: "Vendor", color: "#EC4899", Icon: Store });
  if (profile.is_creator) badges.push({ key: "creator", label: "Creator", color: "#A78BFA", Icon: Sparkles });

  return (
    <div className="rounded-3xl overflow-hidden border border-white/10 bg-zinc-950" data-testid="profile-header">
      {/* Banner — Smart Auto Fit renders full image with blurred backdrop
          filling any letterbox space; fill_crop covers the whole banner. */}
      <div className="relative w-full aspect-[3/1] sm:aspect-[4/1] bg-gradient-to-br from-pink-500/25 via-purple-500/20 to-cyan-500/20" data-testid="profile-banner">
        {bannerUrl && (
          <SmartImage
            src={bannerUrl}
            alt="banner"
            mode={bannerMode}
            testid="profile-banner-image"
            className="w-full h-full"
          />
        )}
        {/* Sheen overlay to keep Hidden Gem premium feel even without a banner upload */}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent pointer-events-none" />
      </div>

      {/* Overlapping avatar + identity */}
      <div className="px-4 sm:px-6 pb-5">
        <div className="flex items-end gap-3 -mt-10 sm:-mt-12">
          <div className="shrink-0 relative">
            <AvatarFrame
              userId={profile.user_id}
              size={96}
              inset={4}
              noFrameClass="bg-pink-500/20 border-2 border-pink-400/40 shadow-[0_0_25px_-6px_rgba(236,72,153,0.6)]"
              testid="profile-header-avatar"
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt={profile.name} className="w-full h-full object-cover" />
              ) : (
                <UserIcon className="w-10 h-10 text-pink-200" />
              )}
            </AvatarFrame>
          </div>
          {/* right-aligned tiny action nub (for public profile follow button) */}
        </div>

        {/* Name row */}
        <div className="mt-3 flex items-start gap-2 flex-wrap">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tighter" data-testid="profile-header-name">
            {profile.name}
          </h1>
          <span className="inline-flex items-center pt-1.5">
            <UserGem userId={profile.user_id} size="md" />
          </span>
          {equippedTitle && (
            <span
              className="mt-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-black border"
              style={{ color: equippedTitle.color, borderColor: `${equippedTitle.color}66`, background: `${equippedTitle.color}1a` }}
              data-testid="profile-header-title"
            >
              {equippedTitle.label}
            </span>
          )}
        </div>

        {/* Handle */}
        {handle && (
          <p className="text-sm text-zinc-400 font-bold" data-testid="profile-header-handle">
            {handle}
          </p>
        )}

        {/* Badges */}
        {badges.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="profile-header-badges">
            {badges.map((b) => (
              <span
                key={b.key}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-black border"
                style={{ color: b.color, borderColor: `${b.color}55`, background: `${b.color}14` }}
                data-testid={`profile-header-badge-${b.key}`}
              >
                <b.Icon className="w-3 h-3" /> {b.label}
              </span>
            ))}
          </div>
        )}

        {/* Bio */}
        {bio && (
          <p className="mt-3 text-sm text-zinc-200 leading-relaxed" data-testid="profile-header-bio">
            {bio}
          </p>
        )}

        {/* Location / socials */}
        {(cp.city || cp.instagram || cp.tiktok || vp.location || vp.instagram || vp.tiktok) && (
          <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
            {(cp.city || vp.location) && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-zinc-300">
                <MapPin className="w-3 h-3" /> {cp.city || vp.location}
              </span>
            )}
            {(cp.instagram || vp.instagram) && (
              <a
                href={`https://instagram.com/${(cp.instagram || vp.instagram).replace(/^@/, "")}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 hover:bg-white/10"
                data-testid="profile-header-instagram"
              >
                <Instagram className="w-3 h-3" /> @{(cp.instagram || vp.instagram).replace(/^@/, "")}
              </a>
            )}
            {(cp.tiktok || vp.tiktok) && (
              <a
                href={`https://tiktok.com/@${(cp.tiktok || vp.tiktok).replace(/^@/, "")}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 hover:bg-white/10"
                data-testid="profile-header-tiktok"
              >
                <Music2 className="w-3 h-3" /> @{(cp.tiktok || vp.tiktok).replace(/^@/, "")}
              </a>
            )}
          </div>
        )}

        {/* Stats row */}
        <div className="mt-4 flex flex-wrap gap-4 sm:gap-6 text-sm">
          <div data-testid="profile-header-posts">
            <span className="font-black text-base">{postsCount}</span>
            <span className="text-zinc-400 ml-1 text-xs">posts</span>
          </div>
          <button
            type="button"
            onClick={() => navigate(`/u/${profile.user_id}/followers`)}
            className="hover:text-pink-300 transition-colors"
            data-testid="profile-header-followers"
          >
            <span className="font-black text-base">{followers}</span>
            <span className="text-zinc-400 ml-1 text-xs">followers</span>
          </button>
          <button
            type="button"
            onClick={() => navigate(`/u/${profile.user_id}/following`)}
            className="hover:text-pink-300 transition-colors"
            data-testid="profile-header-following"
          >
            <span className="font-black text-base">{following}</span>
            <span className="text-zinc-400 ml-1 text-xs">following</span>
          </button>
        </div>

        {/* Action slot */}
        {actions && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="profile-header-actions">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
